import React from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { Box } from '@tmap/mmm-style-guide/src/Box';
import { Button } from '@tmap/mmm-style-guide/src/Button';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import { Reviews, LocationOn } from '@tmap/mmm-style-guide/src/Icon';
import { styled } from '@tmap/mmm-style-guide/src/styled';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';

import 'swiper/css';

import LinkCard from '../linkCard';
import ConditionalLazyLoad from '../conditional-lazy-load';
import ContributionMedia from './contributionMedia';
import CardHeader from './cardHeader';
import { CardProfile } from './cardProfile';
import IconWithLabel from './iconWithLabel';

const ViewMoreBox = styled(Box)(({ theme }) => ({
  height: '100%',
  width: 'calc(100% - 4px)',
  position: 'absolute',
  top: 0,
  opacity: '70%',
  textAlign: 'center',
  backgroundColor: theme.palette.grey[900],
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.common.white,
}));

const MediaContainer = styled(Grid)(() => ({
  position: 'relative',
  '& .MuiSvgIcon-root': {
    maxWidth: '50%',
    maxHeight: '50%',
  },
}));

const ReplyTypography = styled(Typography)(() => ({
  fontSize: '12px',
  whiteSpace: 'nowrap',
}));

const StyledReviewsIcon = styled(Reviews)(({ theme }) => ({
  fontSize: '20px',
  color: theme.palette.grey[300],
  marginRight: theme.spacing(1),
}));

const FooterContainer = styled(Grid, {
  shouldForwardProp: (p) => p !== 'squashVerticalPadding',
})(({ theme, squashVerticalPadding }) => ({
  height: '100%',
  paddingTop: squashVerticalPadding ? theme.spacing(1) : theme.spacing(4),
}));

const ReplyCountContainer = styled(Box)(() => ({
  display: 'flex',
  flexWrap: 'nowrap',
  flexDirection: 'row',
}));

function UGCQuestionBody(props) {
  const {
    contribution,
    squashVerticalPadding = false,
    showPinned = true,
    wantAnswerButton = true,
    previewText,
    previewTextLonger,
    lazyProps = {},
  } = props;

  const {
    media = [],
    author,
    location,
  } = contribution;

  const additionalMediaCount = media.length > 3 ? media.length - 2 : 0;

  const userId = useSelector((state) => state?.users?.current?.userId, shallowEqual);
  const userIsAuthor = contribution.authorId === userId;

  return (
    <>
      <Grid container display='block' rowSpacing={2} direction='column'>
        <Grid item>
          <CardHeader
            contribution={contribution}
            moderationView={false}
            showMenuNearText={false}
            showTopics
            showPinned={showPinned}
          />
        </Grid>
        <Grid item>
          <Typography variant='subtitle1' fontWeight={500}>
            {previewText}{previewTextLonger.length > previewText.length && previewText.length !== 0 && '...'}
          </Typography>
        </Grid>
        {media.length > 0 && (
          <Grid item container direction='row' columnSpacing={0.5}>
            {media.slice(0, 3).map((item, i) => (
              <MediaContainer item xs={4} key={`${contribution._id}-img-${i}`}>
                <ConditionalLazyLoad shouldLazyLoad {...lazyProps}>
                  <ContributionMedia media={item} previewOnly />
                  {additionalMediaCount > 0 && i === 2 && (
                    <ViewMoreBox>
                      <Typography variant='caption'>
                        +{additionalMediaCount} More
                      </Typography>
                    </ViewMoreBox>
                  )}
                </ConditionalLazyLoad>
              </MediaContainer>
            ))}
          </Grid>
        )}
        {location?.description && (
          <Grid item>
            <IconWithLabel
              Icon={LocationOn}
              label={(
                <>
                  <Typography component='span' fontSize='14px' fontWeight={500} color='textPrimary'>Location</Typography>
                  <br />
                  <Typography fontSize='12px'>{location.description}</Typography>
                </>
              )}
            />
          </Grid>
        )}
        <Grid item>
          {wantAnswerButton ? (
            <CardProfile
              author={author}
              contribution={contribution}
              showDate
              datePrefix='Asked'
              maxBadges={{ desktop: 1 }}
            />
          ) : (
            <Grid
              container
              justifyContent='space-between'
              alignItems='end'
              columnSpacing={1}
              rowSpacing={1}
            >
              <Grid item xs={8}>
                <CardProfile
                  author={author}
                  contribution={contribution}
                  showDate
                  datePrefix='Asked'
                  maxBadges={{ desktop: 1 }}
                />
              </Grid>
              <Grid item xs display='flex' justifyContent='end'>
                {
                  userIsAuthor || contribution.numberOfComments > 0 ? (
                    <ReplyCountContainer>
                      <StyledReviewsIcon />
                      <ReplyTypography color='textSecondary'>
                        {contribution.numberOfComments || 0} Replies
                      </ReplyTypography>
                    </ReplyCountContainer>
                  ) : (
                    <ReplyTypography color='grey.500'>
                      Be the first to respond!
                    </ReplyTypography>
                  )
                }
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
      {wantAnswerButton && (
        <LinkCard.Footer>
          <FooterContainer container justifyContent='space-between' alignItems='flex-end' squashVerticalPadding={squashVerticalPadding}>
            <Grid item container justifyContent='space-between' alignItems='center'>
              <Grid item>
                {
                  userIsAuthor || contribution.numberOfComments > 0 ? (
                    <ReplyCountContainer>
                      <StyledReviewsIcon />
                      <ReplyTypography color='textSecondary'>
                        {contribution.numberOfComments || 0} Replies
                      </ReplyTypography>
                    </ReplyCountContainer>
                  ) : (
                    <ReplyTypography color='grey.500'>
                      Be the first to respond!
                    </ReplyTypography>
                  )
                }
              </Grid>
              {!userIsAuthor && (
                <Grid item>
                  <Button variant='contained' color='inverted' disableElevation>
                    Answer
                  </Button>
                </Grid>
              )}
            </Grid>
          </FooterContainer>
        </LinkCard.Footer>
      )}
    </>
  );
}

export default UGCQuestionBody;
